'use client';

import { type FC, useEffect, useMemo, useState } from 'react';

import { DrawerFloatingContext } from './context/drawer-floating.context';
import { DrawerRootContext } from './context/drawer-root.context';
import { useDrawer } from './hooks/use-drawer';
import type { DrawerRootPropsInterface } from './types/drawer-root.types';

/**
 * @since 10.22.0
 */
export const DrawerRoot: FC<DrawerRootPropsInterface> = (props: DrawerRootPropsInterface) => {
  const { children, container, placement = 'right', ...rest } = props;

  const { drawer, floating } = useDrawer(rest);

  /*
   * A `container` ref can belong to an ancestor, so its `current` may still be null
   * on the first render. Read it after commit and store the node, since
   * Floating UI's portal only reads its `root` when it first creates the node.
   */
  const containerRef = container && 'current' in container ? container : null;
  const [refContainerNode, setRefContainerNode] = useState<HTMLElement | ShadowRoot | null>(null);
  useEffect(() => {
    if (containerRef) {
      setRefContainerNode(containerRef.current);
    }
  }, [containerRef]);

  const containerNode =
    container && 'current' in container ? refContainerNode : (container ?? null);
  const contained = Boolean(container);
  const contextValue = useMemo(
    () => ({ contained, containerNode, drawer, placement }),
    [contained, containerNode, drawer, placement],
  );

  return (
    <DrawerRootContext.Provider value={contextValue}>
      <DrawerFloatingContext.Provider value={floating}>{children}</DrawerFloatingContext.Provider>
    </DrawerRootContext.Provider>
  );
};

DrawerRoot.displayName = 'Drawer.Root';
